(() => {
  const ROOT_SELECTOR = '[data-mosha-subscription-selector]';
  const existingRuntime = window.MoshaSubscriptionSelectorRuntime;

  if (existingRuntime) {
    existingRuntime.initialize(document);
    return;
  }

  const instances = new Map();

  class MoshaSubscriptionSelector {
    constructor(root) {
      this.root = root;
      this.formId = root.dataset.productFormId;
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.defaultSellingPlanId = String(root.dataset.sellingPlanId || '');
      this.servingCount = Number(root.dataset.servingCount || 1);
      this.currencyCode = root.dataset.currencyCode || '';
      this.billingSuffix = root.dataset.billingSuffix || '';
      this.servingSuffix = root.dataset.servingSuffix || '/ serving';
      this.debugEnabled =
        root.dataset.debugEnabled === 'true' ||
        Boolean(window.Shopify && window.Shopify.designMode);
      this.productData = this.parseProductData();
      this.abortController = new AbortController();
      this.formAbortController = null;
      this.observer = null;
      this.form = null;
      this.variantInput = null;
      this.isReady = false;

      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleOptionKeydown = this.handleOptionKeydown.bind(this);
      this.handleFormChange = this.handleFormChange.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      if (!this.formId) {
        this.debug('missing product form id');
        return;
      }

      this.bindOptionEvents();
      this.bindDocumentEvents();
      this.isReady = true;
      this.refreshFormReferences();
      this.updateForVariant();
    }

    bindOptionEvents() {
      const signal = this.abortController.signal;

      this.root.querySelectorAll('[data-mosha-option]').forEach((option) => {
        option.addEventListener('click', this.handleOptionClick, { signal });
        option.addEventListener('keydown', this.handleOptionKeydown, { signal });
      });
    }

    bindDocumentEvents() {
      document.addEventListener('variant:change', this.handleVariantChange, {
        signal: this.abortController.signal,
      });
    }

    bindFormEvents() {
      this.formAbortController?.abort();
      this.formAbortController = new AbortController();

      if (!this.form) return;

      const signal = this.formAbortController.signal;
      this.form.addEventListener('change', this.handleFormChange, { signal });
      this.form.addEventListener('submit', this.handleFormSubmit, { signal });

      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.updateForVariant());

      if (this.variantInput) {
        this.observer.observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value'],
        });
      }
    }

    refreshFormReferences() {
      const nextForm = document.getElementById(this.formId);

      if (nextForm !== this.form) {
        this.form = nextForm;
        this.variantInput = this.form?.querySelector('[name="id"]') || null;
        this.bindFormEvents();
      } else {
        this.variantInput = this.form?.querySelector('[name="id"]') || null;
      }

      return this.form;
    }

    getVariantId() {
      this.refreshFormReferences();
      return String(this.variantInput?.value || '');
    }

    getVariant(variantId = this.getVariantId()) {
      return this.productData?.variants?.find(
        (item) => String(item.id) === String(variantId)
      );
    }

    getAllocation(variantId = this.getVariantId()) {
      const variant = this.getVariant(variantId);
      const allocation = variant?.selling_plan_allocations?.find(
        (item) => String(item.selling_plan_id) === this.defaultSellingPlanId
      );

      if (!variant?.available || !allocation) return null;

      return {
        ...allocation,
        variant_price: Number(variant.price),
        serving_price: Math.round(Number(allocation.price) / Math.max(this.servingCount, 1)),
      };
    }

    getSellingPlanInputs() {
      this.refreshFormReferences();
      if (!this.form) return [];
      return Array.from(this.form.querySelectorAll('input[name="selling_plan"]'));
    }

    ensureSellingPlanInput(planId) {
      if (!this.refreshFormReferences()) return null;

      const inputs = this.getSellingPlanInputs();
      let input = inputs.shift();
      inputs.forEach((duplicate) => duplicate.remove());

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        this.form.appendChild(input);
      }

      input.type = 'hidden';
      input.name = 'selling_plan';
      input.value = String(planId);
      return input;
    }

    removeSellingPlanInputs() {
      this.getSellingPlanInputs().forEach((input) => input.remove());
    }

    selectOption(optionName) {
      if (optionName === 'subscription') {
        const allocation = this.getAllocation();

        if (!allocation || String(allocation.selling_plan_id) !== this.defaultSellingPlanId) {
          this.removeSellingPlanInputs();
          this.setRadioState('onetime');
          this.debug('subscription allocation unavailable');
          return;
        }

        this.ensureSellingPlanInput(allocation.selling_plan_id);
        this.setRadioState('subscription');
      } else {
        this.removeSellingPlanInputs();
        this.setRadioState('onetime');
      }

      this.debug('option changed');
    }

    setRadioState(selectedName) {
      this.root.dataset.selectedOption = selectedName;

      this.root.querySelectorAll('[data-mosha-option]').forEach((option) => {
        const isSelected = option.dataset.moshaOption === selectedName;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-checked', String(isSelected));
        option.tabIndex = isSelected ? 0 : -1;
      });

      this.updateDisplayedOffer();
    }

    updateForVariant() {
      const allocation = this.getAllocation();
      const subscriptionOption = this.root.querySelector('[data-mosha-option="subscription"]');
      const unavailable = this.root.querySelector('[data-mosha-unavailable]');
      const selectedOption = this.root.dataset.selectedOption || 'subscription';

      if (!allocation || String(allocation.selling_plan_id) !== this.defaultSellingPlanId) {
        subscriptionOption?.setAttribute('hidden', '');
        if (this.debugEnabled) {
          unavailable?.removeAttribute('hidden');
        } else {
          unavailable?.setAttribute('hidden', '');
        }
        this.removeSellingPlanInputs();
        this.setRadioState('onetime');
        this.updatePrices(null);
        return;
      }

      subscriptionOption?.removeAttribute('hidden');
      subscriptionOption?.setAttribute('aria-disabled', 'false');
      unavailable?.setAttribute('hidden', '');
      this.updatePrices(allocation);
      this.selectOption(selectedOption);
    }

    updatePrices(allocation) {
      const subscriptionPrice = this.root.querySelector('[data-mosha-subscription-price]');
      const billingPrice = this.root.querySelector('[data-mosha-billing-price]');
      const servingPrice = this.root.querySelector('[data-mosha-serving-price]');
      const oneTimePrice = this.root.querySelector('[data-mosha-onetime-price]');
      const oneTimeComparePrice = this.root.querySelector('[data-mosha-onetime-compare-price]');
      const variant = this.getVariant();

      if (allocation) {
        if (subscriptionPrice) subscriptionPrice.textContent = this.formatMoney(allocation.price);
        if (billingPrice) billingPrice.textContent = this.formatMoney(allocation.price);
        if (servingPrice) servingPrice.textContent = this.formatMoney(allocation.serving_price);
        this.root.dataset.variantPrice = String(allocation.variant_price);
      }

      if (oneTimePrice && variant?.price != null) {
        oneTimePrice.textContent = this.formatMoney(Number(variant.price));
      }

      if (
        oneTimeComparePrice &&
        Number(variant?.compare_at_price) > Number(variant?.price)
      ) {
        oneTimeComparePrice.textContent = this.formatMoney(Number(variant.compare_at_price));
        oneTimeComparePrice.removeAttribute('hidden');
      } else {
        oneTimeComparePrice?.setAttribute('hidden', '');
      }

      this.updateDisplayedOffer();
    }

    updateDisplayedOffer() {
      const selectedOption = this.root.dataset.selectedOption || 'subscription';
      const subscriptionPrice = this.root.querySelector('[data-mosha-subscription-price]');
      const frequency = this.root.querySelector('.mosha-subscription-selector__frequency');
      const billing = this.root.querySelector('.mosha-subscription-selector__billing');
      const serving = this.root.querySelector('.mosha-subscription-selector__serving');
      const variant = this.getVariant();
      const allocation = this.getAllocation();
      const currency = this.currencyCode ? ` ${this.currencyCode}` : '';

      if (selectedOption === 'onetime' && variant?.price != null) {
        const price = Number(variant.price);
        const servingPrice = Math.round(price / Math.max(this.servingCount, 1));
        if (subscriptionPrice) subscriptionPrice.textContent = this.formatMoney(price);
        if (frequency) frequency.textContent = '';
        if (billing) billing.textContent = 'Billed once';
        if (serving) {
          serving.textContent = `${this.formatMoney(servingPrice)}${currency} ${this.servingSuffix}`;
        }
        return;
      }

      if (!allocation) return;

      if (subscriptionPrice) subscriptionPrice.textContent = this.formatMoney(allocation.price);
      if (frequency) frequency.textContent = '/mo';
      if (billing) {
        billing.textContent = `Billed ${this.formatMoney(allocation.price)}${currency} ${this.billingSuffix}`.trim();
      }
      if (serving) {
        serving.textContent = `${this.formatMoney(allocation.serving_price)}${currency} ${this.servingSuffix}`;
      }
    }

    cleanInvalidSellingPlan() {
      const allocation = this.getAllocation();
      const validPlanId = allocation ? String(allocation.selling_plan_id) : '';
      const inputs = this.getSellingPlanInputs();
      const sellingPlanValueBeforeCleanup = inputs[0]?.value || '';
      const selectedSubscription = this.root.dataset.selectedOption === 'subscription';
      const hasExactlyOneValidInput =
        inputs.length === 1 &&
        inputs[0].value === validPlanId &&
        validPlanId === this.defaultSellingPlanId;

      if (selectedSubscription && hasExactlyOneValidInput) {
        this.debug('native submit allowed', sellingPlanValueBeforeCleanup);
        return;
      }

      if (inputs.length > 0) {
        this.removeSellingPlanInputs();
      }

      if (selectedSubscription) {
        this.setRadioState('onetime');
      }

      this.debug('native submit allowed after invalid plan cleanup', sellingPlanValueBeforeCleanup);
    }

    handleOptionClick(event) {
      const option = event.currentTarget;

      this.selectOption(option.dataset.moshaOption);
    }

    handleOptionKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.selectOption(event.currentTarget.dataset.moshaOption);
    }

    handleFormChange(event) {
      if (event.target.matches('[name="id"]')) {
        this.updateForVariant();
      }
    }

    handleFormSubmit() {
      this.cleanInvalidSellingPlan();
    }

    handleVariantChange(event) {
      if (event.detail?.sectionId && !this.root.closest(`#shopify-section-${event.detail.sectionId}`)) {
        return;
      }

      this.updateForVariant();
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'CAD',
      }).format(Number(cents) / 100);
    }

    parseProductData() {
      const productJson = this.root.querySelector('[data-mosha-product-json]');

      try {
        return JSON.parse(productJson?.textContent || '{}');
      } catch (error) {
        if (this.debugEnabled) {
          console.warn('Mosha selector could not parse product data.', error);
        }
        return null;
      }
    }

    debug(reason, sellingPlanValueBeforeCleanup = null) {
      if (!this.debugEnabled) return;

      const allocation = this.getAllocation();
      const inputs = this.getSellingPlanInputs();

      console.table({
        reason,
        formFound: Boolean(this.form),
        formId: this.formId,
        selectedOption: this.root.dataset.selectedOption || 'uninitialized',
        variantId: this.getVariantId(),
        sellingPlanInputCount: inputs.length,
        sellingPlanValueBeforeCleanup,
        sellingPlanValue: inputs[0]?.value || '',
        allocationValid:
          Boolean(allocation) &&
          String(allocation?.selling_plan_id) === this.defaultSellingPlanId,
        nativeSubmitAllowed: true,
      });
    }

    destroy() {
      this.abortController.abort();
      this.formAbortController?.abort();
      this.observer?.disconnect();
      this.root.removeAttribute('data-selected-option');
      this.isReady = false;
    }
  }

  function rootsWithin(scope) {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  }

  function initialize(scope = document) {
    instances.forEach((instance, root) => {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });

    rootsWithin(scope).forEach((root) => {
      if (instances.has(root)) return;

      const instance = new MoshaSubscriptionSelector(root);
      if (instance.isReady) instances.set(root, instance);
    });
  }

  function destroyWithin(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  window.MoshaSubscriptionSelectorRuntime = {
    initialize,
    destroyWithin,
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  initialize(document);
})();
